// A source's length when the server does not know it. A library video's `duration_ms` stays
// null until the media worker measures it (S2-lite), and for good where its header gives none,
// so the editor reads the length from the file's own metadata: a detached <video> loads only the
// metadata from the signed URL and gives its duration and its picture's size, as live's editor
// reads them (#1052). Cross-origin is fine: a media element reads them without CORS.
import { useQueries, useQuery, type QueryClient, type QueryKey } from "@tanstack/react-query";

/** What a source's length is: known, being read from the file, or unreadable. */
export type SourceLength =
  | { state: "known"; ms: number }
  | { state: "reading" }
  | { state: "unknown" };

/** What a file's metadata says: its length in whole milliseconds and its picture's size. */
export interface FileFacts {
  ms: number;
  /** Null without a picture (a sound). */
  width: number | null;
  height: number | null;
}

/** A video's length and picture size, the server's or its file's, as `SourceLength` reads it. */
export type SourceFacts =
  | ({ state: "known" } & FileFacts)
  | { state: "reading" }
  | { state: "unknown" };

/** How long a file's metadata may take before its length counts as unknown. */
export const LENGTH_TIMEOUT_MS = 15_000;

/**
 * The file's duration in whole milliseconds and its picture's size. Refuses a file without a
 * finite length, one that fails to load, and one whose metadata does not arrive within
 * `timeoutMs`.
 */
export function readFacts(
  url: string,
  signal?: AbortSignal,
  timeoutMs: number = LENGTH_TIMEOUT_MS,
): Promise<FileFacts> {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    video.preload = "metadata";
    video.muted = true;
    const stop = () => {
      clearTimeout(timer);
      video.removeEventListener("loadedmetadata", loaded);
      video.removeEventListener("error", failed);
      video.removeAttribute("src");
      video.load();
    };
    const loaded = () => {
      const { duration: seconds, videoWidth: width, videoHeight: height } = video;
      stop();
      const pictured = width > 0 && height > 0;
      if (Number.isFinite(seconds) && seconds > 0) {
        resolve({
          ms: Math.round(seconds * 1000),
          width: pictured ? width : null,
          height: pictured ? height : null,
        });
      } else reject(new Error("The video has no length in its metadata."));
    };
    const failed = () => {
      stop();
      reject(new Error("The video could not be read."));
    };
    const timer = setTimeout(() => {
      stop();
      reject(new Error("The video's metadata did not arrive in time."));
    }, timeoutMs);
    video.addEventListener("loadedmetadata", loaded);
    video.addEventListener("error", failed);
    signal?.addEventListener("abort", () => {
      stop();
      reject(new DOMException("Aborted", "AbortError"));
    });
    video.src = url;
    video.load();
  });
}

/** The file's duration in whole milliseconds (`readFacts`). */
export function readLength(
  url: string,
  signal?: AbortSignal,
  timeoutMs: number = LENGTH_TIMEOUT_MS,
): Promise<number> {
  return readFacts(url, signal, timeoutMs).then((facts) => facts.ms);
}

/** A length the server gives: a positive one; null or zero means it does not know. */
export function serverKnows(knownMs: number | null): knownMs is number {
  return knownMs !== null && knownMs > 0;
}

/** One read per source, kept for the session: a file's length does not change. */
function lengthQuery(key: string, url: string | null, knownMs: number | null) {
  return {
    queryKey: ["editor", "source-length", key] as QueryKey,
    queryFn: ({ signal }: { signal: AbortSignal }) => readFacts(url ?? "", signal),
    enabled: !serverKnows(knownMs) && url !== null,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: Number.POSITIVE_INFINITY,
    retry: false,
  };
}

/**
 * A source's length read from its file outside a component (the assistant's add, spec
 * assistant-file-length): `useSourceLength`'s own read, so the bin and the assistant never read one
 * file twice.
 */
export function fetchSourceLength(client: QueryClient, key: string, url: string): Promise<number> {
  const { queryKey, queryFn, staleTime, gcTime, retry } = lengthQuery(key, url, null);
  return client.fetchQuery({ queryKey, queryFn, staleTime, gcTime, retry }).then((facts) => facts.ms);
}

function lengthOf(
  knownMs: number | null,
  url: string | null,
  read: { data: FileFacts | undefined; isError: boolean },
): SourceLength {
  if (serverKnows(knownMs)) return { state: "known", ms: knownMs };
  if (read.data !== undefined) return { state: "known", ms: read.data.ms };
  return url === null || read.isError ? { state: "unknown" } : { state: "reading" };
}

/** A source's length: the server's, else read from the file at `url`. */
export function useSourceLength(key: string, url: string | null, knownMs: number | null) {
  return lengthOf(knownMs, url, useQuery(lengthQuery(key, url, knownMs)));
}

/**
 * A video's length and picture size: the server's, else its file's. The file is read (the same
 * read as `useSourceLength`'s) when the server lacks either; the server's length wins, and its
 * size as a pair. A failed read leaves the size unknown, and the length too without the server's.
 */
export function useSourceFacts(
  key: string,
  url: string | null,
  known: { ms: number | null; width: number | null; height: number | null },
): SourceFacts {
  const sized = known.width !== null && known.height !== null;
  const read = useQuery({
    ...lengthQuery(key, url, known.ms),
    enabled: url !== null && !(serverKnows(known.ms) && sized),
  });
  const size = sized
    ? { width: known.width, height: known.height }
    : { width: read.data?.width ?? null, height: read.data?.height ?? null };
  const settled = sized || url === null || read.data !== undefined || read.isError;
  if (serverKnows(known.ms) && settled) return { state: "known", ms: known.ms, ...size };
  if (read.data !== undefined) return { state: "known", ms: read.data.ms, ...size };
  return url === null || read.isError ? { state: "unknown" } : { state: "reading" };
}

/** The lengths of several sources, by key, read from their files where the server has none. */
export function useSourceLengths(
  sources: readonly { key: string; url: string | null; knownMs: number | null }[],
): ReadonlyMap<string, SourceLength> {
  return useQueries({
    queries: sources.map(({ key, url, knownMs }) => lengthQuery(key, url, knownMs)),
    combine: (reads) =>
      new Map(
        reads.map((read, at) => {
          const { key, url, knownMs } = sources[at]!;
          return [key, lengthOf(knownMs, url, read)] as const;
        }),
      ),
  });
}
